import React, {createContext, useCallback, useContext, useEffect, useReducer} from "react";
import {useSearchParams} from "react-router-dom";

import * as api from "../../services/api";
import {quoteIndexMapper, quoteMapper} from "../../mapper/quoteMapper";
import {useFeedbackTools} from "../../components/FeedbackTools";
import useQuoteUX from "./useQuoteUX";
import usePagination from "./usePagination";
import {DateFormatter} from "../../services/date";
import QuoteService from "../../services/quotes";
import useAccommodations from "../../components/Accommodations/useAccommodations";
import {useBookingCreate} from "../Bookings/query";
import useAccommodationUnitSettings from "../../hooks/useAccommodationUnitSettings";
import {ISOString} from "../../services/date/formatters";
import {isValidDateObject} from "../../services/date/validators";


const QuotesContext = createContext();
export const useQuotes = () => useContext(QuotesContext);

const selectedQuoteInitialState = {
    selectedQuote: quoteMapper({}),
    isLoadingSelectedQuote: true,
    isUpdatingQuote: false, // whether is updating to confirm the selected quote
}

export default function QuotesProvider({children}) {
    // It is not ideal to use the search params here, but it is the only way to permorm the
    // first fetch with the correct filters coming from a link (or navigate).
    // The other option is to une a useEffect but couses two fetches, the initial one
    // and the one triggered one the searchFilter gets updated.
    // TODO: use react query to fix this problem by defining the useQuery inside the componend that
    // needs it.
    const [searchParams] = useSearchParams();
    const createdByAutoReply = searchParams.get("created_by_auto_reply");
    let initialFilter = {}
    if (createdByAutoReply !== null ){
        initialFilter = {created_by_auto_reply: createdByAutoReply === "true"}
    }

    const {getCheckInFrom, getCheckOutBefore} = useAccommodationUnitSettings();

    const [state, dispatcher] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'setAttributes':
                    return {...state, ...action.attributes}
                case 'createQuote':
                    return {...state, quotes: [action.quote, ...state.quotes]}
                case 'updateSelectedQuote':
                    return {...state, selectedQuote: {...state.selectedQuote, ...action.attributes}}
                default:
                    throw new Error(`Cannot find action ${action.type} for quotesState reducer`);
            }
        },
        {
            searchFilters: initialFilter,
            ...selectedQuoteInitialState
        }
    );

    // Quotes UX logic
    const quoteUXstate = useQuoteUX();

    // Apartment data
    const accommodations = useAccommodations();

    // Feedback tools
    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();

    const bookingCreate = useBookingCreate();

    const getQuotes = useCallback(
        async (params) => {
            try {
                const data = await api.Quotes.getAllQuotes({...state.searchFilters, ...params});
                return {
                    itemsCount: data.itemsCount,
                    pageItems: data.pageItems.map(q => quoteIndexMapper(q))
                }
            } catch (e) {
                showErrorNotification(e);
                return {itemsCount: 0, pageItems: []}
            }
        },
        [state.searchFilters]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const quotesPagination = usePagination({
        getPageItems: getQuotes,
        pageSize: 10,
        initializeOnMount: false
    });

    const createQuote = useCallback(
        async (data) => {
            const quote = await api.Quotes.createQuote(data);
            quotesPagination.initialize();
            return quoteMapper(quote);
        },
        [quotesPagination.initialize]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    const deleteSelectedQuote = useCallback(
        async () => {
            await api.Quotes.deleteQuote(state.selectedQuote.id);
            quotesPagination.initialize();  // after the deletion the items in each page change, so we need to reset all
        },
        [state.selectedQuote?.id, quotesPagination.initialize]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const updateEmailMessage = async (emailMessageData=null) => {
        if(emailMessageData){
            dispatcher({type: 'updateSelectedQuote', attributes: {emailMessage: emailMessageData}});
        } else{
            const emailMessageData = await api.Quotes.getEmailMessage(state.selectedQuote.id);
            dispatcher({type: 'updateSelectedQuote', attributes: {emailMessage: emailMessageData}});
        }
    }

    const setSearchFilters = useCallback((searchFilters) => {
            dispatcher({type: "setAttributes", attributes: {searchFilters: searchFilters}});
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const getQuote = useCallback(
        async (quoteId) => {
            if (quoteId !== null && quoteId !== undefined) {
                const quote = await api.Quotes.getQuote(quoteId);
                return quoteMapper(quote);
            } else {
                throw Error(`Expected a valid quoteId got ${quoteId}`);
            }
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    );

    const loadSelectedQuote = useCallback(
        async () => {
            if (quoteUXstate.selectedQuoteId !== null) {
                dispatcher({type: 'setAttributes', attributes: {isLoadingSelectedQuote: true}});
                try {
                    const quote = await getQuote(quoteUXstate.selectedQuoteId);
                    dispatcher({
                        type: 'setAttributes',
                        attributes: {isLoadingSelectedQuote: false, selectedQuote: quote}
                    });
                } catch (e) {
                    showErrorNotification(e);
                    dispatcher({type: "setAttributes", attributes: {isLoadingSelectedQuote: false}})
                }
            }
        },
        [getQuote, quoteUXstate.selectedQuoteId]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const confirmSelectedQuote = useCallback(
        async (quoteVariant, options={}) => {
            dispatcher({type: 'setAttributes', attributes: {isUpdatingQuote: true}});

            const accommodationUnitIds = quoteVariant.quoteItems.map(i => i.apartmentId);
            const checkInFrom = getCheckInFrom({accommodationUnitIds});
            const checkOutBefore = getCheckOutBefore({accommodationUnitIds});

            const booking = {
                bookingItemsAttributes: quoteVariant.quoteItems.map(i => ({
                    price: i.getPrice(),
                    bookableType: "Apartment",
                    bookableId: i.apartmentId,
                    arrival: DateFormatter.onlyDateISOString(i.arrival, null),
                    departure: DateFormatter.onlyDateISOString(i.departure, null),
                })),
                adults: state.selectedQuote.adults,
                children: state.selectedQuote.children,
                petsNumber: state.selectedQuote.petsNumber,
                email: state.selectedQuote.email,
                name: state.selectedQuote.name,
                surname: state.selectedQuote.surname,
                nationality: state.selectedQuote.nationality ? state.selectedQuote.nationality.code : null,

                bookingFinanceAttributes: {
                    deposit: state.selectedQuote.bookingCondition.confirmationPolicy === QuoteService.enums.CONFIRMATION_POLICY.deposit ?
                        Math.trunc(quoteVariant.getPriceCent() * state.selectedQuote.bookingCondition.depositPercentage / 100 / 100)
                        :
                        0,
                },

                bookingConditionAttributes: {
                    cancellationPolicy: state.selectedQuote.bookingCondition.cancellationPolicy,
                    touristTaxPolicy: state.selectedQuote.bookingCondition.touristTaxPolicy,
                    refundableUntil: state.selectedQuote.bookingCondition.refundableUntil ? DateFormatter.ISOString(state.selectedQuote.bookingCondition.refundableUntil, null) : null,
                    checkInFrom: isValidDateObject(checkInFrom) ? DateFormatter.timeString(checkInFrom) : null,
                    checkOutBefore: isValidDateObject(checkOutBefore) ? DateFormatter.timeString(checkOutBefore) : null,
                },

                bookingConfirmationDocumentAttributes: {
                    sendEmailAfterCreate: options.sendEmailAfterCreate || false,
                }
            }

            try {
                await bookingCreate.mutateAsync({data: booking});
                const data = await api.Quotes.updateQuote(
                    state.selectedQuote.id,
                    {quoteVariants: {id: quoteVariant.id, confirmed: true}}
                );
                quotesPagination.updateItem({id: state.selectedQuote.id, confirmed: true});
                dispatcher({
                    type: 'setAttributes',
                    attributes: {selectedQuote: quoteMapper(data), isUpdatingQuote: false}
                });
                showSuccessNotification("Preventivo confermato");
            } catch (e) {
                showErrorNotification(e);
                dispatcher({type: 'setAttributes', attributes: {isUpdatingQuote: false}});
            }
        },
        [state.selectedQuote, quotesPagination.updateItem]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const resetSelectedQuote = useCallback(
        () => {
            dispatcher({type: 'setAttributes', attributes: selectedQuoteInitialState});
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(
        () => {
            loadSelectedQuote();
        },
        [loadSelectedQuote]
    );

    useEffect(
        () => {
            if (quoteUXstate.selectedQuoteId === null) {
                resetSelectedQuote();
            }
        },
        [resetSelectedQuote, quoteUXstate.selectedQuoteId]
    )

    const context = {
        searchFilters: state.searchFilters,
        isLoadingQuotes: state.isLoadingQuotes,
        selectedQuote: state.selectedQuote,
        isLoadingSelectedQuote: state.isLoadingSelectedQuote,
        isUpdatingQuote: state.isUpdatingQuote,
        quotesPagination,
        setSearchFilters,
        createQuote,
        ...quoteUXstate,
        accommodations,
        confirmSelectedQuote,
        deleteSelectedQuote,
        resetSelectedQuote,
        updateEmailMessage,
    }

    return (
        <QuotesContext.Provider value={context}>
            {children}
        </QuotesContext.Provider>
    )
}